/**
 * Fetch Returning Visit Data Actor
 *
 * Fetches upcoming appointments for a returning patient so the kiosk can
 * present them with a list of visits to check in to.
 *
 * Uses `getRichFutureAppointmentsByConsumerId` from the PC service.
 */

import { fromPromise } from "xstate";
import { z } from "zod";
import { getRichFutureAppointmentsByConsumerId } from "../../server-actions/pc-actions";
import type { ActorMetadata, ActorWithMetadata } from "./send-sms.actor";

/**
 * Input Schema — what this actor needs from machine context.
 */
const FetchReturningVisitDataInputSchema = z.object({
  consumerId: z.string(),
  practiceId: z.string().optional(),
});

type FetchReturningVisitDataInput = z.infer<
  typeof FetchReturningVisitDataInputSchema
>;

/**
 * Success Schema — what this actor returns on success.
 */
const FetchReturningVisitDataSuccessSchema = z.object({
  appointments: z.array(
    z.object({
      id: z.string(),
      treatmentName: z.string(),
      bookedDate: z.string(),
      slotId: z.string(),
      ehrAppointmentId: z.string(),
    }),
  ),
});

type FetchReturningVisitDataSuccess = z.infer<
  typeof FetchReturningVisitDataSuccessSchema
>;

/**
 * Error Schema
 */
const FetchReturningVisitDataErrorSchema = z.object({
  code: z.enum(["FETCH_FAILED", "NETWORK_ERROR"]),
  message: z.string(),
  retryable: z.boolean(),
});

type FetchReturningVisitDataError = z.infer<
  typeof FetchReturningVisitDataErrorSchema
>;

/**
 * Core logic — exported for direct unit testing without XState internals.
 */
export async function fetchReturningVisitData(
  input: FetchReturningVisitDataInput,
): Promise<FetchReturningVisitDataSuccess> {
  const validated = FetchReturningVisitDataInputSchema.safeParse(input);

  if (!validated.success) {
    const error = new Error(
      `fetchReturningVisitData validation failed: ${validated.error.message}`,
    );
    throw error;
  }

  const { consumerId, practiceId } = validated.data;

  try {
    const data = await getRichFutureAppointmentsByConsumerId(consumerId);

    const appointments = data
      .filter((item) => item.appointmentWithSlots.slots.length > 0)
      .map((item) => {
        // Filter by practice location if available, otherwise use first slot
        const slot = practiceId
          ? item.appointmentWithSlots.slots.find(
              (s) => String(s.practiceId) === practiceId,
            )
          : item.appointmentWithSlots.slots[0];
        if (!slot) return null;
        return {
          id: item.appointmentWithSlots.id,
          treatmentName: slot.boughtTreatments[0]?.name ?? "Appointment",
          bookedDate: slot.startTime,
          slotId: slot.id,
          ehrAppointmentId: slot.EhrSlot?.ehrAppointmentId ?? "",
        };
      })
      .filter((appt): appt is NonNullable<typeof appt> => appt !== null)
      .filter((appt) => appt.slotId !== "");

    return { appointments };
  } catch (error) {
    const wrappedError: FetchReturningVisitDataError = {
      code: "NETWORK_ERROR",
      message: error instanceof Error ? error.message : "Network error",
      retryable: true,
    };
    throw wrappedError;
  }
}

const fetchReturningVisitDataActorImpl = fromPromise(
  ({ input }: { input: FetchReturningVisitDataInput }) =>
    fetchReturningVisitData(input),
);

/**
 * Fetch Returning Visit Data Actor with Metadata
 */
export const fetchReturningVisitDataActor: ActorWithMetadata = {
  impl: fetchReturningVisitDataActorImpl,
  metadata: {
    displayName: "fetchReturningVisitData",
    description:
      "Fetches upcoming appointments for a returning patient to select from",
    category: "data",
    retryable: true,
    timeout: 10000,
    errorCodes: ["FETCH_FAILED", "NETWORK_ERROR"],
    inputSchema: FetchReturningVisitDataInputSchema,
    successSchema: FetchReturningVisitDataSuccessSchema,
    errorSchema: FetchReturningVisitDataErrorSchema,
    onSuccess: (result: unknown) => {
      const success = result as FetchReturningVisitDataSuccess;
      return {
        returningVisitAppointments: success.appointments,
      };
    },
    onError: (error: unknown) => {
      const err = error as FetchReturningVisitDataError;
      return {
        returningVisitError: {
          code: err.code,
          message: err.message,
          retryable: err.retryable,
        },
      };
    },
  } satisfies ActorMetadata,
};
